import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Page } from '../../layout/AppLayout.jsx'
import Card from '../../components/ui/Card.jsx'
import Badge from '../../components/ui/Badge.jsx'
import Button, { IconButton } from '../../components/ui/Button.jsx'
import Icon from '../../components/ui/Icon.jsx'
import Pagination from '../../components/ui/Pagination.jsx'
import { Input } from '../../components/ui/Field.jsx'
import { useFeedback } from '../../components/ui/feedback.jsx'
import { useCompany } from '../../context/CompanyContext.jsx'
import api from '../../lib/api.js'
import PartyFormModal from './PartyFormModal.jsx'

/*
 * Customers / suppliers list — inventory §3.1 & §4.1. Server-side search +
 * pagination; create/edit via the internal modal; (de)activation through the
 * internal confirm modal. `customer.invoice_count > 0` ⇒ soft-deactivate (keeps
 * history), else hard delete.
 */
export default function PartyListPage({ config }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { confirm, toast } = useFeedback()
  const { currentCompanyId } = useCompany()

  const [rows, setRows] = useState([])
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 })
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(20)
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(true)
  const [modal, setModal] = useState(null) // {entityId, initial} | {} for new
  const [sort, setSort] = useState(null) // { key, dir } — server-side over the whole set

  const toggleSort = (key) => {
    setPage(1)
    setSort((s) => (s?.key !== key ? { key, dir: 'asc' } : s.dir === 'asc' ? { key, dir: 'desc' } : null))
  }

  const fetchRows = useCallback(() => {
    if (!currentCompanyId) return undefined
    setLoading(true)
    const params = { page, per_page: perPage, search: search.trim() || undefined }
    if (sort) params.sort = `${sort.key}_${sort.dir}`
    return api.get(`${config.endpoint}/`, { params })
      .then(({ data }) => {
        setRows(data?.[config.arrayKey] || [])
        setPagination(data?.pagination || { page: 1, pages: 1, total: 0 })
      })
      .finally(() => setLoading(false))
  }, [config, currentCompanyId, page, perPage, search, sort])

  useEffect(() => { fetchRows() }, [fetchRows])

  const onSearch = (v) => { setSearch(v); setPage(1) }

  const remove = async (row) => {
    const soft = row.invoice_count > 0
    const ok = await confirm({
      tone: 'danger', destructive: true,
      title: t(soft ? 'parties.confirm.deactivateTitle' : 'parties.confirm.deleteTitle'),
      message: t(soft ? 'parties.confirm.deactivateMessage' : 'parties.confirm.deleteMessage', { name: row.name, count: row.invoice_count }),
      confirmLabel: t(soft ? 'parties.actions.deactivate' : 'parties.actions.delete'),
      cancelLabel: t('common.cancel'),
    })
    if (!ok) return
    try { await api.delete(`${config.endpoint}/${row.id}`); toast(t(soft ? 'parties.toast.deactivated' : 'parties.toast.deleted'), 'success'); fetchRows() }
    catch { toast(t('parties.toast.error'), 'error') }
  }

  const reactivate = async (row) => {
    try { await api.post(`${config.endpoint}/${row.id}/reactivate`); toast(t('parties.toast.reactivated'), 'success'); fetchRows() }
    catch { toast(t('parties.toast.error'), 'error') }
  }

  const action = (
    <Button variant="primary" icon="plus" onClick={() => setModal({})}>{t(`parties.${config.key}.new`)}</Button>
  )
  const th = (label, align = 'left', sortKey = null) => {
    const active = sortKey && sort?.key === sortKey
    const base = { textAlign: align, padding: '12px 18px', fontSize: 11.5, fontWeight: 600, color: active ? 'var(--color-primary)' : 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '.04em', whiteSpace: 'nowrap' }
    if (!sortKey) return <th style={base}>{label}</th>
    return (
      <th style={{ ...base, cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort(sortKey)} title={t('common.sort')}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, justifyContent: align === 'right' ? 'flex-end' : 'flex-start' }}>
          {label}<span style={{ opacity: active ? 1 : 0.35, fontSize: 10 }}>{active ? (sort.dir === 'asc' ? '▲' : '▼') : '↕'}</span>
        </span>
      </th>
    )
  }

  return (
    <Page title={t(`parties.${config.key}.title`)} action={action}>
      <Card style={{ marginBottom: 18 }}>
        <div style={{ position: 'relative', maxWidth: 360 }}>
          <span style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-muted)' }}><Icon name="search" size={17} /></span>
          <Input value={search} onChange={(e) => onSearch(e.target.value)} placeholder={t(`parties.${config.key}.searchPlaceholder`)} style={{ height: 42, paddingLeft: 38 }} aria-label={t('common.search')} />
        </div>
      </Card>

      <Card padding={0}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--color-text-muted)' }}>{t('common.loading')}</div>
        ) : rows.length === 0 ? (
          <div style={{ padding: '52px 28px', textAlign: 'center' }}>
            <div style={{ color: 'var(--color-text-muted)', display: 'flex', justifyContent: 'center', marginBottom: 12 }}><Icon name={config.key === 'customers' ? 'customers' : 'supplier'} size={38} strokeWidth={1.5} /></div>
            <div style={{ fontFamily: 'var(--font-title)', fontWeight: 700, color: 'var(--color-ink)', marginBottom: 14 }}>{t(search ? `parties.${config.key}.emptyFiltered` : `parties.${config.key}.empty`)}</div>
            {!search && <div style={{ display: 'flex', justifyContent: 'center' }}><Button variant="primary" icon="plus" onClick={() => setModal({})}>{t(`parties.${config.key}.new`)}</Button></div>}
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 700 }}>
              <thead><tr style={{ background: 'var(--color-bg-lilac)' }}>
                {th(t('parties.columns.name'), 'left', 'name')}{th(t('parties.columns.vat'), 'left', 'vat_number')}{th(t('parties.columns.contact'), 'left', 'email')}{th(t('parties.columns.city'), 'left', 'city')}{th(t('common.actions'), 'right')}
              </tr></thead>
              <tbody>
                {rows.map((r) => {
                  const inactive = r.is_active === false
                  const go = () => navigate(`${config.endpoint}/${r.id}`.replace(/^\//, '/app/'))
                  return (
                    <tr key={r.id} style={{ borderTop: '1px solid var(--color-border-row)', opacity: inactive ? 0.55 : 1 }}>
                      <td style={{ padding: '12px 18px', fontWeight: 600, color: 'var(--color-ink)', cursor: 'pointer' }} onClick={go}>
                        {r.name}{inactive && <span style={{ marginLeft: 8 }}><Badge tone="neutral">{t('parties.inactive')}</Badge></span>}
                      </td>
                      <td style={{ padding: '12px 18px', color: 'var(--color-text)' }}>{r.vat_number || '—'}</td>
                      <td style={{ padding: '12px 18px', color: 'var(--color-text)' }}>{[r.contact_first_name, r.contact_last_name].filter(Boolean).join(' ') || '—'}</td>
                      <td style={{ padding: '12px 18px', color: 'var(--color-text)' }}>{r.city || '—'}</td>
                      <td style={{ padding: '12px 18px' }}>
                        <div style={{ display: 'flex', gap: 7, justifyContent: 'flex-end' }}>
                          <IconButton icon="eye" title={t('parties.actions.details')} onClick={go} />
                          {!inactive && <IconButton icon="pencil" variant="primary" title={t('parties.actions.edit')} onClick={() => setModal({ entityId: r.id, initial: r })} />}
                          {!inactive && <IconButton icon="power" title={t('parties.actions.deactivate')} variant="danger" onClick={() => remove(r)} />}
                          {inactive && <IconButton icon="check" title={t('parties.actions.reactivate')} variant="primary" onClick={() => reactivate(r)} />}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
        {!loading && rows.length > 0 && (
          <div style={{ padding: '0 18px 16px' }}>
            <Pagination page={pagination.page} totalPages={pagination.pages} total={pagination.total} unit={t(`parties.${config.key}.unit`)} perPage={perPage}
              onPage={(p) => { setPage(p); window.scrollTo({ top: 0, behavior: 'smooth' }) }} onPerPage={(n) => { setPerPage(n); setPage(1) }} />
          </div>
        )}
      </Card>

      <PartyFormModal open={!!modal} config={config} entityId={modal?.entityId} initial={modal?.initial}
        onClose={() => setModal(null)} onSaved={() => { toast(t('parties.toast.saved'), 'success'); fetchRows() }} />
    </Page>
  )
}
